<template>
  <div class="sales-by-employee">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-account-chart"></i> Sales by Employee Analysis
            </h2>
            <p class="text-white opacity-75">Comprehensive employee performance analysis and insights</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Advanced Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>

        <el-form :inline="true" size="small" label-width="100px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
                <el-date-picker
                  v-model="filters.dateRange"
                  type="daterange"
                  range-separator="to"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="handleDateChange"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Employees">
                <el-select
                  v-model="filters.user_id"
                  multiple
                  filterable
                  placeholder="All Employees"
                  @change="loadEmployeeData"
                >
                  <el-option
                    v-for="user in users"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Customers">
                <el-select
                  v-model="filters.customer_id"
                  multiple
                  filterable
                  placeholder="All Customers"
                  @change="loadEmployeeData"
                >
                  <el-option
                    v-for="customer in customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Payment Methods">
                <el-select
                  v-model="filters.payment_method_id"
                  multiple
                  filterable
                  placeholder="All Payment Methods"
                  @change="loadEmployeeData"
                >
                  <el-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :label="method.name"
                    :value="method.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Sort By">
                <el-select
                  v-model="filters.sort_by"
                  placeholder="Sort by"
                  @change="loadEmployeeData"
                >
                  <el-option label="Revenue (High to Low)" value="total_revenue_desc"></el-option>
                  <el-option label="Revenue (Low to High)" value="total_revenue_asc"></el-option>
                  <el-option label="Orders (High to Low)" value="total_orders_desc"></el-option>
                  <el-option label="Orders (Low to High)" value="total_orders_asc"></el-option>
                  <el-option label="Avg Order Value (High to Low)" value="average_order_value_desc"></el-option>
                  <el-option label="Avg Order Value (Low to High)" value="average_order_value_asc"></el-option>
                  <el-option label="Employee Name" value="user_name"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Items Per Page">
                <el-select
                  v-model="filters.per_page"
                  placeholder="Items per page"
                  @change="loadEmployeeData"
                >
                  <el-option label="10" :value="10"></el-option>
                  <el-option label="25" :value="25"></el-option>
                  <el-option label="50" :value="50"></el-option>
                  <el-option label="100" :value="100"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadEmployeeData" :loading="loading">
                  Search
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- Summary Statistics -->
      <el-row :gutter="20" class="m-b-20">
        <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
          <el-card :body-style="{ padding: '20px' }" class="stat-card">
            <div class="stat-content">
              <div class="stat-info">
                <h4 class="stat-title">{{ stat.title }}</h4>
                <h2 class="stat-value">{{ stat.value }}</h2>
                <div class="stat-comparison">
                  <span :class="['trend', stat.trend > 0 ? 'positive' : 'negative']">
                    <i :class="stat.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(stat.trend) }}%
                  </span>
                  <span class="comparison-text">vs previous period</span>
                </div>
              </div>
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading employee analysis...</p>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Charts Section -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Employee Performance Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-bar"></i> Employee Performance</h4>
                <el-select v-model="chartView" size="mini" @change="updateChartData">
                  <el-option label="Revenue" value="revenue"></el-option>
                  <el-option label="Orders" value="orders"></el-option>
                  <el-option label="Avg Order Value" value="average_order_value"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="employeeData.length > 0"
                  :chart-data="employeeChartData"
                  :chart-options="employeeChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-bar" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Revenue Share Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-pie"></i> Revenue Share</h4>
                <el-switch v-model="showPercentage" active-text="%" inactive-text="Value"/>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="employeeData.length > 0"
                  :chart-data="revenueShareChartData"
                  :chart-options="revenueShareChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-pie" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Top Employees List -->
        <el-row :gutter="20" class="m-b-20">
          <el-col :xs="24" :lg="8" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-trophy"></i> Top Employees</h4>
              </div>
              <div class="top-employees-list">
                <div
                  v-for="(employee, index) in topEmployees"
                  :key="employee.user_id"
                  class="employee-item"
                >
                  <div class="employee-rank">{{ index + 1 }}</div>
                  <div class="employee-info">
                    <div class="employee-name">{{ employee.user_name }}</div>
                    <div class="employee-stats">
                      <span class="revenue">{{ formatCurrency(employee.total_revenue) }}</span>
                      <span class="orders">{{ employee.total_orders }} orders</span>
                    </div>
                    <el-progress
                      :percentage="(employee.total_revenue / Math.max(...topEmployees.map(e => e.total_revenue))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="employee-trend">
                    <span :class="['growth', employee.growth_rate > 0 ? 'positive' : 'negative']">
                      <i :class="employee.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(employee.growth_rate) }}%
                    </span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Trends Chart -->
          <el-col :xs="24" :lg="16" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-line"></i> Employee Trends</h4>
                <el-select v-model="trendPeriod" size="mini" @change="loadTrendsData">
                  <el-option label="Daily" value="daily"></el-option>
                  <el-option label="Weekly" value="weekly"></el-option>
                  <el-option label="Monthly" value="monthly"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <line-chart
                  v-if="trendsData.length > 0"
                  :chart-data="trendsChartData"
                  :chart-options="trendsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-line" style="font-size: 48px;"></i>
                  <p>No trends data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Employee Data Table -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-table"></i> Employee Analysis</h4>
            <div class="header-actions">
              <el-tag type="info">{{ pagination.total }} employees found</el-tag>
              <el-button-group>
                <el-button size="mini" @click="toggleViewMode">
                  <i :class="viewMode === 'table' ? 'el-icon-menu' : 'el-icon-s-grid'"></i>
                  {{ viewMode === 'table' ? 'Grid' : 'Table' }}
                </el-button>
              </el-button-group>
            </div>
          </div>

          <!-- Table View -->
          <div v-if="viewMode === 'table'">
            <el-table
              :data="employeeData"
              style="width: 100%"
              size="small"
              @sort-change="handleSortChange"
              @selection-change="handleSelectionChange"
            >
              <el-table-column type="selection" width="55" />
              <el-table-column prop="user_name" label="Employee" width="200" />
              <el-table-column label="Revenue" width="120" sortable="custom">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.total_revenue) }}
                </template>
              </el-table-column>
              <el-table-column label="Orders" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_orders.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Avg Order Value" width="120">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.average_order_value) }}
                </template>
              </el-table-column>
              <el-table-column label="Unique Customers" width="120">
                <template slot-scope="scope">
                  {{ scope.row.unique_customers.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Completion Rate" width="120">
                <template slot-scope="scope">
                  {{ scope.row.completion_rate.toFixed(1) }}%
                </template>
              </el-table-column>
              <el-table-column label="Growth" width="100">
                <template slot-scope="scope">
                  <span :class="['growth', scope.row.growth_rate > 0 ? 'positive' : 'negative']">
                    <i :class="scope.row.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(scope.row.growth_rate) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" fixed="right">
                <template slot-scope="scope">
                  <el-button-group>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-view"
                      @click="viewEmployeeDetails(scope.row)"
                    />
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-s-data"
                      @click="viewEmployeeTrends(scope.row)"
                    />
                  </el-button-group>
                </template>
              </el-table-column>
            </el-table>

            <!-- Pagination -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.current_page"
                :page-sizes="[10, 25, 50, 100]"
                :page-size="+pagination.per_page"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
              />
            </div>
          </div>

          <!-- Grid View -->
          <div v-else class="grid-view">
            <el-row :gutter="20">
              <el-col
                v-for="employee in employeeData"
                :key="employee.user_id"
                :xs="24"
                :sm="12"
                :md="8"
                :lg="6"
                class="m-b-20"
              >
                <el-card :body-style="{ padding: '15px' }" class="employee-card">
                  <div class="employee-header">
                    <h4 class="employee-name">{{ employee.user_name }}</h4>
                    <el-tag :type="getEmployeeStatusType(employee)" size="mini">
                      {{ getEmployeeStatusText(employee) }}
                    </el-tag>
                  </div>

                  <div class="employee-info">
                    <div class="info-row">
                      <span class="label">Revenue:</span>
                      <span class="value amount">{{ formatCurrency(employee.total_revenue) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Orders:</span>
                      <span class="value">{{ employee.total_orders.toLocaleString() }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Avg Order Value:</span>
                      <span class="value">{{ formatCurrency(employee.average_order_value) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Unique Customers:</span>
                      <span class="value">{{ employee.unique_customers.toLocaleString() }}</span>
                    </div>
                  </div>

                  <div class="employee-footer">
                    <span class="growth" :class="employee.growth_rate > 0 ? 'positive' : 'negative'">
                      <i :class="employee.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(employee.growth_rate) }}%
                    </span>
                    <div class="actions">
                      <el-button size="mini" type="primary" icon="el-icon-view" @click="viewEmployeeDetails(employee)" />
                      <el-button size="mini" type="success" icon="el-icon-s-data" @click="viewEmployeeTrends(employee)" />
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Employee Details Dialog -->
    <el-dialog
      title="Employee Details"
      :visible.sync="employeeDetailsVisible"
      width="70%"
      :before-close="handleCloseDialog"
    >
      <div v-if="selectedEmployeeDetails" class="employee-details">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>Employee Information</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Name">{{ selectedEmployeeDetails.employee.name }}</el-descriptions-item>
              <el-descriptions-item label="Email">{{ selectedEmployeeDetails.employee.email }}</el-descriptions-item>
              <el-descriptions-item label="Joined">{{ formatDate(selectedEmployeeDetails.employee.created_at) }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
          <el-col :span="12">
            <h4>Performance Metrics</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Total Revenue">{{ formatCurrency(selectedEmployeeDetails.sales_stats.total_revenue) }}</el-descriptions-item>
              <el-descriptions-item label="Total Orders">{{ selectedEmployeeDetails.sales_stats.total_orders.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Avg Order Value">{{ formatCurrency(selectedEmployeeDetails.sales_stats.average_order_value) }}</el-descriptions-item>
              <el-descriptions-item label="Unique Customers">{{ selectedEmployeeDetails.sales_stats.unique_customers.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Last Sale">{{ formatDate(selectedEmployeeDetails.sales_stats.last_sale_date) }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
        </el-row>

        <el-divider content-position="left">Top Items</el-divider>
        <el-table :data="selectedEmployeeDetails.top_items || []" size="small">
          <el-table-column prop="item_name" label="Item" />
          <el-table-column prop="total_quantity" label="Qty" width="80" />
          <el-table-column prop="total_revenue" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.total_revenue) }}
            </template>
          </el-table-column>
          <el-table-column prop="total_orders" label="Orders" width="100" />
        </el-table>

        <el-divider content-position="left">Top Customers</el-divider>
        <el-table :data="selectedEmployeeDetails.top_customers || []" size="small">
          <el-table-column prop="customer_name" label="Customer" />
          <el-table-column prop="total_orders" label="Orders" width="80" />
          <el-table-column prop="total_revenue" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.total_revenue) }}
            </template>
          </el-table-column>
          <el-table-column prop="average_order_value" label="Avg Order Value" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.average_order_value) }}
            </template>
          </el-table-column>
        </el-table>

        <el-divider content-position="left">Commission</el-divider>
        <div v-if="employeeCommission">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="Commission Rate">{{ employeeCommission.commission_rate }}%</el-descriptions-item>
            <el-descriptions-item label="Commission">{{ formatCurrency(employeeCommission.commission_amount) }}</el-descriptions-item>
            <el-descriptions-item label="Bonus">{{ formatCurrency(employeeCommission.bonus_amount) }}</el-descriptions-item>
            <el-descriptions-item label="Total Earnings">{{ formatCurrency(employeeCommission.total_earnings) }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="employeeDetailsVisible = false">Close</el-button>
        <el-button type="primary" @click="viewEmployeeTrends(selectedEmployee)">View Trends</el-button>
        <el-button type="success" @click="exportEmployeeReport(selectedEmployee)">Export Report</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/_Partials/Charts/BarChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'
import LineChart from '@/components/_Partials/Charts/LineChart.vue'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'SalesByEmployee',
  components: {
    BarChart,
    DoughnutChart,
    LineChart
  },
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      loading: false,
      exporting: false,
      viewMode: 'table',
      employeeDetailsVisible: false,
      selectedEmployee: null,
      selectedEmployeeDetails: null,
      employeeCommission: null,
      selectedEmployees: [],
      chartView: 'revenue',
      showPercentage: false,
      trendPeriod: 'monthly',
      employeeData: [],
      trendsData: [],
      users: [],
      customers: [],
      paymentMethods: [],
      filters: {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        sort_by: 'total_revenue_desc',
        per_page: 25
      },
      pagination: {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      },
      dateShortcuts: [{
        text: 'Last 7 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: 'Last 30 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: 'Last 3 months',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 3)
          return [start, end]
        }
      }, {
        text: 'This year',
        value: () => {
          const end = new Date()
          const start = new Date(new Date().getFullYear(), 0, 1)
          return [start, end]
        }
      }]
    }
  },
  computed: {
    summaryStats () {
      const totalRevenue = this.employeeData.reduce((sum, e) => sum + e.total_revenue, 0)
      const totalOrders = this.employeeData.reduce((sum, e) => sum + e.total_orders, 0)
      const avgOrderValue = this.employeeData.length > 0 ? (totalRevenue / totalOrders) : 0
      const uniqueCustomers = this.employeeData.reduce((sum, e) => sum + e.unique_customers, 0)
      const avgCompletion = this.employeeData.length > 0 ? (this.employeeData.reduce((sum, e) => sum + e.completion_rate, 0) / this.employeeData.length) : 0
      const avgGrowth = this.employeeData.length > 0 ? (this.employeeData.reduce((sum, e) => sum + e.growth_rate, 0) / this.employeeData.length) : 0
      return [
        {
          title: 'Total Revenue',
          value: this.formatCurrency(totalRevenue),
          trend: 12.5,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Orders',
          value: totalOrders.toLocaleString(),
          trend: 8.3,
          icon: 'mdi mdi-receipt'
        },
        {
          title: 'Avg Order Value',
          value: this.formatCurrency(avgOrderValue),
          trend: 5.2,
          icon: 'mdi mdi-chart-line'
        },
        {
          title: 'Avg Completion',
          value: avgCompletion.toFixed(1) + '%',
          trend: avgCompletion,
          icon: 'mdi mdi-check-circle-outline'
        },
        {
          title: 'Avg Growth',
          value: avgGrowth.toFixed(1) + '%',
          trend: avgGrowth,
          icon: 'mdi mdi-trending-up'
        }
      ]
    },
    topEmployees () {
      return this.employeeData.slice(0, 5)
    },
    employeeChartData () {
      const labels = this.employeeData.slice(0, 10).map(e => e.user_name)
      const data = this.employeeData.slice(0, 10).map(e => {
        switch (this.chartView) {
          case 'revenue':
            return e.total_revenue
          case 'orders':
            return e.total_orders
          case 'average_order_value':
            return e.average_order_value
          default:
            return e.total_revenue
        }
      })
      return {
        labels: labels,
        datasets: [{
          label: this.chartView.charAt(0).toUpperCase() + this.chartView.slice(1),
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: this.getChartColors(data.length),
          borderWidth: 1
        }]
      }
    },
    employeeChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                if (this.chartView === 'revenue' || this.chartView === 'average_order_value') {
                  return this.formatCurrency(value)
                }
                return value.toLocaleString()
              }.bind(this)
            }
          }
        }
      }
    },
    revenueShareChartData () {
      const labels = this.employeeData.slice(0, 8).map(e => e.user_name)
      const data = this.employeeData.slice(0, 8).map(e => {
        if (this.showPercentage) {
          const total = this.employeeData.reduce((sum, i) => sum + i.total_revenue, 0)
          return ((e.total_revenue / total) * 100).toFixed(1)
        }
        return e.total_revenue
      })
      return {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: '#fff',
          borderWidth: 2
        }]
      }
    },
    revenueShareChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        }
      }
    },
    trendsChartData () {
      return {
        labels: this.trendsData.map(item => item.period),
        datasets: this.employeeData.slice(0, 5).map((e, index) => ({
          label: e.user_name,
          data: this.trendsData.filter(t => t.user_id === e.user_id).map(t => t.revenue),
          borderColor: this.getChartColors(5)[index],
          backgroundColor: this.getChartColors(5)[index] + '20',
          tension: 0.4
        }))
      }
    },
    trendsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.loadInitialData()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
      }
    },
    resetFilters () {
      this.filters = {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        sort_by: 'total_revenue_desc',
        per_page: 25
      }
      this.pagination.current_page = 1
      this.loadEmployeeData()
    },
    async loadInitialData () {
      try {
        await Promise.all([
          this.loadUsers(),
          this.loadCustomers(),
          this.loadPaymentMethods()
        ])
        await this.loadEmployeeData()
      } catch (error) {
        this.handleError(error, 'Failed to load initial data')
      }
    },
    async loadEmployeeData () {
      this.loading = true
      try {
        const params = {
          ...this.filters,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }
        const response = await this.$reportService.getEmployeePerformance(params)
        if (response.status === 'success') {
          this.employeeData = response.data
          this.pagination = {
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to load employee data')
      } finally {
        this.loading = false
      }
    },
    async loadTrendsData () {
      try {
        const params = {
          ...this.filters,
          period: this.trendPeriod,
          per_page: 1000,
          page: 1
        }
        const response = await this.$reportService.getEmployeeTrends(params)
        if (response.status === 'success') {
          this.trendsData = response.data
        }
      } catch (error) {
        console.error('Error loading trends data:', error)
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadCustomers () {
      try {
        this.customers = await this.$customerService.getCustomers()
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        this.paymentMethods = await this.$paymentMethodService.getMethods()
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async exportData () {
      this.exporting = true
      try {
        const params = {
          ...this.filters,
          dl: 'pdf'
        }
        const response = await this.$reportService.getEmployeePerformance(params)
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.exporting = false
      }
    },
    updateChartData () {
      // Chart data will be updated automatically through computed properties
    },
    handleSortChange ({ column, prop, order }) {
      // Handle table sorting
      this.loadEmployeeData()
    },
    handleSelectionChange (selection) {
      this.selectedEmployees = selection
    },
    handleSizeChange (size) {
      this.pagination.per_page = size
      this.loadEmployeeData()
    },
    handleCurrentChange (page) {
      this.pagination.current_page = page
      this.loadEmployeeData()
    },
    toggleViewMode () {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table'
    },
    viewEmployeeDetails (employee) {
      this.selectedEmployee = employee
      this.employeeDetailsVisible = true
      this.loadEmployeeDetails(employee)
    },
    async loadEmployeeDetails (employee) {
      try {
        const response = await this.$reportService.getEmployeeDetails({ user_id: employee.user_id, ...this.filters })
        if (response.status === 'success') {
          this.selectedEmployeeDetails = response.data
          // Load commission
          const commissionResp = await this.$reportService.getEmployeeCommission({ user_id: employee.user_id, ...this.filters })
          if (commissionResp.status === 'success') {
            this.employeeCommission = commissionResp.data
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to load employee details')
      }
    },
    viewEmployeeTrends (employee) {
      // Could open a modal or navigate to a trends view
      console.log('Viewing trends for employee:', employee.user_name)
    },
    exportEmployeeReport (employee) {
      // Implement employee-specific report export
      console.log('Exporting report for employee:', employee.user_name)
    },
    handleCloseDialog () {
      this.employeeDetailsVisible = false
      this.selectedEmployee = null
      this.selectedEmployeeDetails = null
      this.employeeCommission = null
    },
    getEmployeeStatusType (employee) {
      if (employee.growth_rate > 10) return 'success'
      if (employee.growth_rate > 0) return 'warning'
      return 'danger'
    },
    getEmployeeStatusText (employee) {
      if (employee.growth_rate > 10) return 'High Growth'
      if (employee.growth_rate > 0) return 'Growing'
      return 'Declining'
    },
    getProgressColor (index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399']
      return colors[index % colors.length]
    },
    getChartColors (count) {
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#9C27B0', '#FF9800', '#795548', '#607D8B', '#FF5722'
      ]
      return colors.slice(0, count)
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
/* ... (reuse styles from SalesByItem.vue and SalesSummary.vue for consistency) ... */
</style>
